import { useState, useRef, useEffect, useMemo } from "react";
import {
  MONTHS_OF_YEAR,
  toMonthKey,
  parseDateString,
  formatDateToISO,
} from "../../../helpers";

export const useMonthRangePicker = ({
  startDate = null,
  endDate = null,
  onChange = () => {},
  placeholder = "Filter by date",
}) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const initialParsed = useMemo(() => {
    return {
      start: parseDateString(startDate),
      end: parseDateString(endDate),
    };
  }, [startDate, endDate]);

  const [selectedStart, setSelectedStart] = useState(initialParsed.start);
  const [selectedEnd, setSelectedEnd] = useState(initialParsed.end);
  const [hoverMonth, setHoverMonth] = useState(null);

  const currentYear = new Date().getFullYear();
  const [displayYear, setDisplayYear] = useState(
    initialParsed.start?.year || currentYear,
  );

  useEffect(() => {
    setSelectedStart(initialParsed.start);
    setSelectedEnd(initialParsed.end);
    if (initialParsed.start?.year) {
      setDisplayYear(initialParsed.start.year);
    }
  }, [initialParsed]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const getEffectiveRange = () => {
    if (!selectedStart) return { startKey: null, endKey: null };
    const startKey = toMonthKey(selectedStart.year, selectedStart.month);

    if (selectedEnd) {
      const endKey = toMonthKey(selectedEnd.year, selectedEnd.month);
      return {
        startKey: Math.min(startKey, endKey),
        endKey: Math.max(startKey, endKey),
      };
    }

    if (hoverMonth) {
      const hoverKey = toMonthKey(hoverMonth.year, hoverMonth.month);
      return {
        startKey: Math.min(startKey, hoverKey),
        endKey: Math.max(startKey, hoverKey),
      };
    }

    return { startKey, endKey: startKey };
  };

  const { startKey: activeStartKey, endKey: activeEndKey } =
    getEffectiveRange();

  const handleMonthClick = (year, month) => {
    if (!selectedStart || (selectedStart && selectedEnd)) {
      setSelectedStart({ year, month });
      setSelectedEnd(null);
    } else {
      const startKey = toMonthKey(selectedStart.year, selectedStart.month);
      const clickedKey = toMonthKey(year, month);
      if (clickedKey < startKey) {
        setSelectedStart({ year, month });
        setSelectedEnd(selectedStart);
      } else {
        setSelectedEnd({ year, month });
      }
    }
  };

  const handleClear = () => {
    setSelectedStart(null);
    setSelectedEnd(null);
    setHoverMonth(null);
    onChange({ startDate: null, endDate: null });
    setIsOpen(false);
  };

  const handleSetDate = () => {
    if (!selectedStart) return;

    const start = selectedStart;
    const end = selectedEnd || selectedStart;

    const startKey = toMonthKey(start.year, start.month);
    const endKey = toMonthKey(end.year, end.month);

    const actualStart = startKey <= endKey ? start : end;
    const actualEnd = startKey <= endKey ? end : start;

    const startISO = formatDateToISO(
      actualStart.year,
      actualStart.month,
      false,
    );
    const endISO = formatDateToISO(actualEnd.year, actualEnd.month, true);

    onChange({ startDate: startISO, endDate: endISO });
    setIsOpen(false);
  };

  const triggerLabel = useMemo(() => {
    if (!initialParsed.start) return placeholder;

    const startLabel = `${MONTHS_OF_YEAR[initialParsed.start.month]} ${initialParsed.start.year}`;
    if (!initialParsed.end) return startLabel;

    const endLabel = `${MONTHS_OF_YEAR[initialParsed.end.month]} ${initialParsed.end.year}`;
    if (startLabel === endLabel) return startLabel;

    return `${startLabel} - ${endLabel}`;
  }, [initialParsed, placeholder]);

  const hasValue = Boolean(initialParsed.start);

  const getMonthCellState = (year, index) => {
    const key = toMonthKey(year, index);
    const isStart = activeStartKey !== null && key === activeStartKey;
    const isEnd = activeEndKey !== null && key === activeEndKey;
    const isSingle = isStart && isEnd;
    const isInRange =
      activeStartKey !== null &&
      activeEndKey !== null &&
      key > activeStartKey &&
      key < activeEndKey;
    const isColStart = index % 3 === 0;
    const isColEnd = index % 3 === 2;

    return {
      isSingle,
      isStart: isStart && !isSingle,
      isEnd: isEnd && !isSingle,
      isInRange,
      isColStart,
      isColEnd,
    };
  };

  return {
    containerRef,
    isOpen,
    setIsOpen,
    displayYear,
    setDisplayYear,
    selectedStart,
    hasValue,
    triggerLabel,
    handleMonthClick,
    handleClear,
    handleSetDate,
    setHoverMonth,
    getMonthCellState,
  };
};

export default useMonthRangePicker;
